<template>
  <div class="mdl-search">
    <MdlSearchbar
      v-model="input.search"
      :searching="searching"
      :disabled="disabled"
      :min-length="minSearchLength"
      search-on-click
      @submit="onSearchSubmit"
    >
      <template #search>
        <CountrySelector
          local
          id="company-country-select"
          :model-value="input.country"
          :disabled="disabled"
          rel="tooltip"
          :title="trans('Select country')"
          @update:model-value="onCountrySelect"
        />
      </template>
    </MdlSearchbar>

    <!-- Result set -->
    <div v-if="data.length > 0" class="mdl-searchbar__results">
      <Company
        v-for="company of data"
        :key="`${company.name}${company.orgnr}${company.id}`"
        selectable
        :selected="isCompanySelected(company)"
        :disabled="!companySelectable(company)"
        :company="company"
        :country="input.country"
        @click="onCompanySelect(company)"
      />
    </div>
    <div v-else-if="searched || emptyMessage" class="mdl-searchbar__results flex flex-col items-center py-8">
      <div v-if="searched">
        <div class="flex-inline opaque">{{ trans('No results found') }}</div>

        <MdlButton v-if="countryModel.company.create" raised @click="gotoCreateScreen" class="mt-4">
          {{ trans('Create new') }}
        </MdlButton>
      </div>
      <div v-else>
        <div v-if="emptyMessage" class="flex-inline opaque">
          {{ emptyMessage }}
        </div>
      </div>
    </div>

    <div class="action-bar">
      <slot name="actions" />

      <MdlPaginator
        v-if="data.length && total > rows"
        :rows-from="offset + 1"
        :rows-to="offset + data.length"
        :rows-total="total"
        :page-rows="rows"
        :page-current="page"
        :page-last="pages"
        @goto="onPaginate"
      />

      <div v-if="showCreateOption" class="flex-grow">
        <MdlDetails :summary="trans('Create new')" v-model="create" />
        <MdlButton v-if="false" icon raised @click.stop="gotoCreateScreen">
          <i class="material-icons">add</i>
        </MdlButton>
      </div>
    </div>

    <!-- Create new company screen -->
    <template v-if="canCreate && create">
      <div class="mdl-searchbar__add">
        <MdlTextfield
          :floating-label="trans('Country')"
          :model-value="`${countryModel.flag} ${countryModel.i18n}`"
          readonly
        />
        <MdlTextfield
          required
          autofocus
          v-model="input.name"
          :floating-label="trans('Name')"
          :minlength="minSearchLength"
          :error="hasCompanyNameError"
          @input="onInput"
        />
        <MdlTextfield :floating-label="trans('Org.nr.')" v-model="input.orgnr" @input="onInput" />
      </div>
    </template>
  </div>
</template>

<script>
import { trans } from '@/munio/i18n'
import Company from './Company.vue'
import CountrySelector from './CountrySelector.vue'
import MdlButton from '@component/mdl/Button.vue'
import MdlDetails from '@component/mdl/Details.vue'
import MdlPaginator from '@component/mdl/Paginator.vue'
import MdlSearchbar from '@component/mdl/Searchbar.vue'
import MdlTextfield from '@component/mdl/Textfield.vue'

export default {
  name: 'CompanySearch',

  components: {
    Company,
    CountrySelector,
    MdlButton,
    MdlDetails,
    MdlPaginator,
    MdlSearchbar,
    MdlTextfield,
  },

  props: {
    country: { type: String, default: Munio.config.company.country },
    modelValue: Object,
    disabled: Boolean,
    context: Boolean,
    emptyMessage: String,
    initialResult: { type: Array, required: false, default: () => [] }, // fallback results, can be result of last search
    except: { type: Array, default: () => [] },
  },

  emits: ['search', 'searched', 'update:modelValue'],

  data() {
    return {
      page: 1,
      rows: 5,
      minSearchLength: 2,
      searchResult: this.getInitialResult(),
      selected: this.getInitialSelected(),
      searched: false,
      searching: false,
      create: false,
      input: this.getDefaultInput(),
    }
  },

  watch: {
    create(value) {
      if (value) {
        this.gotoCreateScreen()
      } else {
        this.onInput()
      }
    },
  },

  computed: {
    countryModel() {
      return Munio.country(this.input.country)
    },

    canCreate() {
      return this.countryModel.company.create
    },

    showCreateOption() {
      if (!this.canCreate) {
        return false
      }

      if (!this.data.length) {
        return false
      }

      return this.page >= this.pages
    },

    offset() {
      return this.rows * (this.page - 1)
    },

    pages() {
      if (!this.searchResult.length) {
        return 1
      }

      return Math.ceil(this.searchResult.length / this.rows)
    },

    data() {
      return this.searchResult?.slice(this.offset, this.offset + this.rows)
    },

    total() {
      return this.searchResult.length
    },

    hasCompanyNameError() {
      return this.input.name.trim().length < this.minSearchLength
        ? trans('Please enter :num or more characters', { num: this.minSearchLength })
        : undefined
    },
  },

  methods: {
    trans,

    isCompanySelected(company) {
      if (this.create) {
        return false
      }

      return this.selected && this.selected === company
    },

    isValueInInitialResult() {
      return this.modelValue && this.initialResult.find((c) => c.id === this.modelValue.id || c === this.modelValue)
    },

    getInitialResult() {
      if (this.modelValue && !this.isValueInInitialResult()) {
        return [this.modelValue, ...this.initialResult]
      }

      if (this.initialResult) {
        return this.initialResult
      }

      return [this.modelValue].filter((v) => !!v)
    },

    getInitialSelected() {
      if (this.modelValue) {
        return this.modelValue
      }

      return null
    },

    companySelectable(company) {
      if (company.id) {
        return !this.except.includes(company.id)
      }

      return true
    },

    getAddress(address) {
      const line1 = address.line1
      const line2 = address.line2
      const line3 = [address.postcode, address.city].filter((v) => v).join(' ')

      return [line1, line2, line3].filter((v) => v).join(', ')
    },

    getDefaultInput() {
      const country = this.modelValue ? this.modelValue.country : this.country

      return {
        search: this.input ? this.input.search : '',
        country: this.input ? this.input.country : country,
        name: this.input ? this.input.name : '',
        orgnr: '',
      }
    },

    async onSearchSubmit(value) {
      this.input.search = value
      this.searchResult = []
      this.page = 1
      this.create = false
      this.selected = null

      if (this.input.search.length >= this.minSearchLength) {
        this.searching = true
        this.$emit('search', this.input.search)
        const { data } = await Munio.api.companies.search(this.input.search, this.input.country, this.context)
        this.searching = false
        this.searched = true
        this.searchResult = data.data
        this.input = this.getDefaultInput()
        this.$emit('searched', data.data)
      }
    },

    onCountrySelect(country) {
      this.input.country = country
    },

    onCompanySelect(company) {
      this.selected = company ?? null
      this.create = false
      this.onInput()
    },

    gotoCreateScreen() {
      this.create = true
      this.input.name = this.input.name || this.input.search
      this.$emit('update:modelValue', null)
      this.onInput()
    },

    onInput() {
      if (this.create) {
        const payload =
          this.input.name.trim().length >= this.minSearchLength
            ? {
                name: this.input.name,
                orgnr: this.input.orgnr,
                country: this.input.country,
              }
            : null

        this.$emit('update:modelValue', payload)
      } else if (this.selected) {
        this.$emit('update:modelValue', this.selected)
      } else {
        this.$emit('update:modelValue', null)
      }
    },

    onPaginate(page) {
      this.input = this.getDefaultInput()
      this.onInput()
      this.page = page
    },

    getFormGroupClasses(field) {
      const error = this.hasError(field)
      const message = this.getError(field)

      return [
        'form-group',
        {
          'has-error': !!error,
          'has-feedback': !!message,
        },
      ]
    },

    hasError(field) {
      return this.form.errors.has(field)
    },

    getError(field) {
      return this.form.errors.get(field)
    },
  },
}
</script>
